import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';

import {
  catchError,
  filter,
  from,
  map,
  Observable,
  of,
  Subject,
  Subscription,
  switchMap, take,
  tap,
} from 'rxjs';

import { Shapes } from '@shared/core';
import { TableSizes } from '@shared/floor-plans';
import { Service } from '@shared/service/models';
import { TableAttributes } from '@shared/floor-plan/models/floor-plan-attributes';
import { FloorPlanSection, FloorPlanTable } from '@shared/floor-plan/models';
import {
  ConfirmationModalComponent, ConfirmationModalResult,
  ValidationService,
} from '@tablesready/common-ui';
import { Resource } from '@shared/resource/models';
import { ButtonColorsEnum } from '@tablesready/button-group-control';
import { FloorPlanTableService } from '../../../../../../../../../services';
import {
  DEFAULT_MAX_VALUES,
  DEFAULT_MIN_VALUES,
  DEFAULT_TABLES_DATA,
} from '../../../../constants';
import { angleChanged } from '../../../objects.methods';
import { DeleteTableModalComponent } from '../delete-table-modal';
import { FrontendModalService, FrontendModalSize } from '@tablesready/frontend-modal';
import { AppIcon } from '@tablesready/frontend-icons';
import { AlertsService } from '@tablesready/frontend-alerts-bar';

enum ResourceSelectMode {
  New = 'New',
  Existing = 'Existing',
}

@Component({
  selector: 'tablesready-add-table-form',
  templateUrl: './add-table-form.component.html',
  styleUrls: ['./add-table-form.component.scss'],
})
export class AddTableFormComponent implements OnInit, OnChanges, OnDestroy {
  @Output() resourceChanged = new EventEmitter<Resource>();
  @Output() formClosed = new EventEmitter<boolean>();
  @Output() tableRemoved = new EventEmitter<string>();
  @Output() nameChanged = new EventEmitter<string>();
  @Output() capacityChanged = new EventEmitter<string>();
  @Output() shapeChanged = new EventEmitter<Shapes>();
  @Output() sizeChanged = new EventEmitter<TableSizes>();
  @Output() rotationAngleChanged = new EventEmitter<number>();
  @Output() formTouched = new EventEmitter<boolean>();

  @Input() services: Service[] = [];
  @Input() resources: Resource[] = [];
  @Input() sections: FloorPlanSection[] = [];
  @Input() size = TableSizes.small;
  @Input() shape = Shapes.square;
  @Input() isEditMode = false;
  @Input() resourceId?: string;
  @Input() sectionId?: string;
  @Input() rotation?: number;
  @Input() width?: number;
  @Input() height?: number;
  @Input() top?: number;
  @Input() left?: number;
  @Input() tableId?: string;
  @Input() floorPlanId?: string;
  @Input() locationId?: string;
  @Input() generatedTableName?: string;
  @Input() duplicatedTable = false;
  @Input() onCreate?: (name: string, attributes: TableAttributes, sectionId?: string) => void;
  @Input() onTableDelete?: (id: string, floorPlanId: string) => void;
  @Input() onTableDuplicate?: (table: FloorPlanTable) => void;
  @Input() onResourceDelete?: (id: string) => void;
  @Input() onSave?: (
    id: string,
    floorPlanId: string,
    tableName: string,
    attributes: TableAttributes,
    sectionId?: string
  ) => void;

  @Input() onResourceCreate?: (resource: Omit<Resource, '_id'>) => Observable<Resource>;
  @Input() onResourceUpdate?: (resource: Partial<Resource>) => Observable<Resource>;

  form?: UntypedFormGroup;
  icon = AppIcon;

  baseResource: Resource;
  newResourceName = '';
  availableResources: Resource[] = [];
  buttonGroupColors = ButtonColorsEnum;
  fullInfoVisible = false;
  tableInfoData = DEFAULT_TABLES_DATA;
  resourceSelectModeEnum = ResourceSelectMode;
  resourceSelectModeControl = new FormControl(ResourceSelectMode.New);
  resourceOptions = [
    {
      name: 'New',
      value: ResourceSelectMode.New,
    },
    {
      name: 'Existing',
      value: ResourceSelectMode.Existing,
    },
  ];
  readonly minCapacityValue = 0;

  private lastTableId = '';
  private readonly setAvailableResourcesForTableId$ = new Subject<string>();
  private readonly subscriptions = new Subscription();

  constructor(
    private readonly fb: FormBuilder,
    private readonly tableService: FloorPlanTableService,
    private readonly validationService: ValidationService,
    private readonly alertsService: AlertsService,
    private readonly modalController: FrontendModalService,
  ) {
    const tablesSub = this.setAvailableResourcesForTableId$
      .pipe(switchMap(currentTableId => this.tableService.floorPlanIdToTablesMap$.pipe(map(floorPlanWithTables => ({
        currentTableId,
        floorPlanWithTables,
      })))))
      .subscribe(({ currentTableId, floorPlanWithTables }) => {
        const usedResourceIds: string[] = [];
        Object.entries(floorPlanWithTables).forEach(([, tables]) => {
          tables.forEach((table) => {
            if (table.id === currentTableId) {
              return;
            }

            usedResourceIds.push(table.resourceId);
          });
        });

        this.availableResources = this.resources.filter(res => res.enabled && !usedResourceIds.includes(res._id));
      });

    this.subscriptions.add(tablesSub);
  }

  setAvailableResourcesForTableId(tableId: string): void {
    this.setAvailableResourcesForTableId$.next(tableId);
  }

  ngOnInit(): void {
    this.fullInfoVisible = !this.isEditMode;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.tableId && this.tableId !== this.lastTableId) {
      this.init();
      this.lastTableId = this.tableId;
    }

    const formPropsToCheck = ['rotation', 'size', 'shape', 'sectionId'];
    formPropsToCheck.forEach((prop) => {
      if (!changes[prop]) {
        return;
      }

      this.form?.get(prop)?.setValue(changes[prop].currentValue);
      if (changes[prop].previousValue !== undefined) {
        this.form?.markAllAsTouched();
        this.formTouched.emit(true);
      }
    });
  }

  duplicateTable(tableId: string): void {
    const table = this.tableService.savedTables.find(item => item.id === tableId);
    if (!table) {
      return;
    }

    this.onTableDuplicate(table);
  }

  delete(tableId: string): void {
    const table = this.tableService.savedTables.find(item => item.id === tableId);
    if (!table) {
      return;
    }

    this.modalController.create<DeleteTableModalComponent, unknown>(
      DeleteTableModalComponent,
      { resourceName: table.name },
      FrontendModalSize.default,
      false,
    )
      .pipe(
        take(1),
        catchError(err => of(err)),
        filter(data => data !== 0 && data !== undefined),
        tap((deleteResources) => {
          if (this.isEditMode && this.onTableDelete !== undefined) {
            this.onTableDelete(this.tableId, this.floorPlanId);
          }

          if (deleteResources && this.onResourceDelete !== undefined) {
            this.onResourceDelete(table.resourceId);
          }

          this.tableRemoved.emit(this.tableId);
          this.formClosed.emit(false);
        }),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  saveTable(): void {
    this.validationService.validateForm(this.form);
    if (!this.onCreate || !this.onSave || !this.onResourceCreate || !this.form.valid) {
      return;
    }

    if (this.resourceSelectModeControl.value === ResourceSelectMode.New && !this.isEditMode) {
      this.createResourceAndSaveTable();

      return;
    }

    const resourceId = this.resourceIdControl?.value;
    if (resourceId === null) {
      return;
    }

    const sectionId = this.sectionIdControl?.value;
    const resource = this.resources.find(res => res._id === resourceId);
    const resourceUpdated = this.resourceWasUpdated(resource);

    if (resourceUpdated) {
      this.updateResourceAndSaveTable(resource);

      return;
    }

    const attributes: TableAttributes = this.getAttributes(resourceId);

    this.lastTableId = undefined;
    if (this.isEditMode) {
      this.onSave(this.tableId, this.floorPlanId, resource.name, attributes, sectionId);
      this.formClosed.emit(false);
    } else {
      this.onCreate(resource.name, attributes, sectionId);
      this.formClosed.emit(false);
    }
  }

  cancel(): void {
    if (this.isEditMode && !this.form.touched) {
      this.formClosed.emit(true);

      return;
    }

    this.modalController.create<ConfirmationModalComponent, ConfirmationModalResult<boolean>>(ConfirmationModalComponent, {
      options: {
        content: 'Leave the form? Changes that you made will not be saved.',
        title: 'Cancel table changes',
        closeBtnText: 'Cancel',
      },
    })
      .pipe(
        take(1),
        filter((res) => res?.response),
        tap(() => {
          if (!this.isEditMode) {
            this.tableRemoved.emit(this.tableId);
          }

          this.formClosed.emit(true);
        }),
      )
      .subscribe();
  }

  resetAngle(): void {
    this.form.markAsTouched();
    this.rotationControl.setValue(0);
    this.rotationAngleChanged.emit(0);
  }

  changeInfoVisibility(): void {
    this.fullInfoVisible = !this.fullInfoVisible;
  }

  resourceSelectModeChange(mode: ResourceSelectMode): void {
    if (mode === ResourceSelectMode.New) {
      this.resourceIdControl.removeValidators(Validators.required);
      this.resourceNameControl.removeValidators(Validators.required);
      this.resourceNameControl.setValue(this.newResourceName);
      this.nameChanged.emit(this.resourceNameControl.value);
    } else {
      this.resourceIdControl.setValidators(Validators.required);
      this.resourceNameControl.setValidators(Validators.required);

      if (this.resourceIdControl.value) {
        const resource = this.resources.find(resource => resource._id === this.resourceIdControl.value);
        this.nameChanged.emit(resource.name);
      } else {
        this.nameChanged.emit(this.newResourceName);
      }
    }

    this.resourceIdControl.updateValueAndValidity();
    this.resourceNameControl.updateValueAndValidity();
  }

  resourceChangeById(resourceId: string): void {
    const resource = this.resources.find(item => item._id === resourceId);
    if (!resource) {
      return;
    }

    this.resourceChange(resource);
  }

  resourceChange(resource: Resource): void {
    if (this.isEditMode) {
      return;
    }

    this.resourceSelectModeChange(this.resourceSelectModeControl.value);

    if (this.resourceSelectModeControl.value === ResourceSelectMode.Existing) {
      const min = resource?.capacity.min || DEFAULT_MIN_VALUES[this.size];
      const max = resource?.capacity.max || DEFAULT_MAX_VALUES[this.size];
      const servicesIds = this.services
        .filter(service => resource?.services.includes(service._id))
        .map(service => service._id);

      this.serviceControl.setValue(servicesIds);
      this.resourceNameControl.setValue(resource?.name || this.newResourceName);
      this.minCapacityControl.setValue(min);
      this.maxCapacityControl.setValue(max);
      this.publicCheckInControl.setValue(resource?.availability.waitlist);
      this.reservationsControl.setValue(resource?.availability.reservations);
      this.resourceChanged.emit(resource);
      this.capacityChanged.emit(`${min}-${max}`);
      this.formTouched.emit(true);

      return;
    }

    this.minCapacityControl.setValue(resource?.capacity.min || DEFAULT_MIN_VALUES[this.size]);
    this.maxCapacityControl.setValue(resource?.capacity.max || DEFAULT_MAX_VALUES[this.size]);
    this.publicCheckInControl.setValue(resource?.availability.waitlist);
    this.reservationsControl.setValue(resource?.availability.reservations);
    this.sectionIdControl.setValue(this.sectionId);
    this.formTouched.emit(true);

    if (this.resourceSelectModeControl.value === ResourceSelectMode.New) {
      this.resourceNameControl.setValue(resource.name);

      return;
    }

    this.serviceControl.setValue([]);
  }

  capacityChange(min: string, max: string): void {
    this.capacityChanged.emit(`${min}-${max}`);
    this.formTouched.emit(true);
  }

  checkMinCapacity(value: number): void {
    if (value < this.minCapacityValue) {
      this.minCapacityControl.setValue(this.minCapacityValue);
    }

    if (value > this.maxCapacityControl.value) {
      this.minCapacityControl.setValue(this.maxCapacityControl.value);
    }
  }

  checkMaxCapacity(value: number): void {
    if (value < this.minCapacityControl.value) {
      this.maxCapacityControl.setValue(this.minCapacityControl.value);
    }
  }

  get resourceNameControl(): AbstractControl {
    return this.form.get('resourceName');
  }

  get resourceIdControl(): AbstractControl {
    return this.form.get('resourceId');
  }

  get minCapacityControl(): AbstractControl {
    return this.form.get('minCapacity');
  }

  get maxCapacityControl(): AbstractControl {
    return this.form.get('maxCapacity');
  }

  get publicCheckInControl(): AbstractControl {
    return this.form.get('availability').get('publicCheckIn');
  }

  get reservationsControl(): AbstractControl {
    return this.form.get('availability').get('reservations');
  }

  get serviceControl(): AbstractControl {
    return this.form.get('service');
  }

  get sectionIdControl(): AbstractControl {
    return this.form.get('sectionId');
  }

  get rotationControl(): AbstractControl {
    return this.form.get('rotation');
  }

  get disableDuplicateBtnTooltip(): string {
    if (this.form.touched) {
      return 'You need to save or discard changes before duplicating this table';
    }

    return '';
  }

  private init(): void {
    this.setAvailableResourcesForTableId(this.tableId);
    this.baseResource = {
      _id: 'baseResource',
      name: this.generatedTableName,
      enabled: true,
      description: '',
      category: '',
      availability: {
        waitlist: true,
        reservations: true,
      },
      capacity: {
        min: DEFAULT_MIN_VALUES[this.size],
        max: DEFAULT_MAX_VALUES[this.size],
      },
      restaurant: this.locationId,
      order: 0,
      services: [],
    };

    let minCapacity = DEFAULT_MIN_VALUES[this.size];
    let maxCapacity = DEFAULT_MAX_VALUES[this.size];
    let resourceServices = this.services.length === 1 ? [this.services[0]._id] : [];
    let publicCheckIn = true;
    let reservations = true;
    let resourceName = '';
    let resourceId = null;
    let capacityMinMaxValidatorValue = DEFAULT_MAX_VALUES[this.size];

    if (this.resourceId !== undefined) {
      const resource = this.resources.find(res => res._id === this.resourceId);
      if (resource) {
        if (this.duplicatedTable) {
          this.baseResource.availability.waitlist = resource.availability.waitlist;
          this.baseResource.availability.reservations = resource.availability.reservations;
          this.baseResource.capacity.min = resource.capacity.min;
          this.baseResource.capacity.max = resource.capacity.max;
        }

        resourceId = this.duplicatedTable ? resourceId : this.resourceId;
        const enabledResourceServices: string[] = [];

        for (const service of this.services) {
          if (resource.services.includes(service._id)) {
            enabledResourceServices.push(service._id);
          }
        }

        minCapacity = resource.capacity.min;
        maxCapacity = resource.capacity.max;
        capacityMinMaxValidatorValue = resource.capacity.max;
        resourceServices = enabledResourceServices;
        publicCheckIn = resource.availability.waitlist;
        reservations = resource.availability.reservations;
        resourceName = this.duplicatedTable ? resourceName : resource.name;
      }
    }

    this.newResourceName = resourceName === '' ? this.baseResource.name : resourceName;
    this.form = this.fb.group({
      resourceId: [resourceId, [Validators.required]],
      resourceName: [resourceName, this.isEditMode ? [Validators.required] : []],
      minCapacity: [minCapacity, [Validators.required, Validators.min(0), Validators.max(capacityMinMaxValidatorValue)]],
      maxCapacity: [maxCapacity, [Validators.required, Validators.min(1)]],
      availability: this.fb.group({
        publicCheckIn: [publicCheckIn],
        reservations: [reservations],
      }),
      service: [resourceServices, [Validators.required]],
      sectionId: [this.sectionId],
      size: [this.size, [Validators.required]],
      shape: [this.shape, [Validators.required]],
      rotation: [this.rotation || 0, [Validators.required, Validators.min(0), Validators.max(360)]],
      width: [this.width || 0],
      height: [this.height || 0],
      top: [this.top || 0],
      left: [this.left || 0],
    });

    this.formTouched.emit(false);

    const resourceNameSub = this.resourceNameControl.valueChanges
      .pipe(
        tap((value: string) => {
          if (this.isEditMode || this.resourceSelectModeControl.value === ResourceSelectMode.New) {
            this.newResourceName = value;
            this.nameChanged.emit(value);
            this.resourceNameControl.markAsTouched();
          }
        }),
      ).subscribe();
    this.subscriptions.add(resourceNameSub);

    const capacityMaxSub = this.maxCapacityControl.valueChanges
      .pipe(tap((max) => {
        this.minCapacityControl.clearValidators();
        this.minCapacityControl.setValidators([
          Validators.min(0),
          Validators.max(max),
          Validators.required,
        ]);
        this.minCapacityControl.updateValueAndValidity();
      })).subscribe();
    this.subscriptions.add(capacityMaxSub);

    if (resourceId === null) {
      this.resourceChange(this.baseResource);
    }

    const publicCheckInSub = this.publicCheckInControl.valueChanges
      .pipe(tap(() => this.form.get('availability').markAsTouched())).subscribe();
    this.subscriptions.add(publicCheckInSub);

    const reservationsSub = this.reservationsControl.valueChanges
      .pipe(tap(() => this.form.get('availability').markAsTouched())).subscribe();
    this.subscriptions.add(reservationsSub);

    const touchedSub = this.form.valueChanges
      .subscribe(() => {
        this.formTouched.emit(this.form.touched);
      });

    this.subscriptions.add(touchedSub);
  }

  private updateResourceAndSaveTable(resource: Resource): void {
    let resourceName = this.resourceNameControl.value;

    if (this.resourceIdControl.value && this.resourceSelectModeControl.value === ResourceSelectMode.Existing) {
      resourceName = this.resources.find(resource => this.resourceIdControl.value === resource._id).name;
    }

    const dataToUpdate: Partial<Resource> = {
      _id: resource._id,
      name: resourceName,
      capacity: {
        min: this.minCapacityControl.value,
        max: this.maxCapacityControl.value,
      },
      availability: {
        waitlist: this.publicCheckInControl.value,
        reservations: this.reservationsControl.value,
      },
      services: this.serviceControl.value,
      description: resource.description,
    };

    if (this.isEditMode) {
      resource.name = dataToUpdate.name;
    }

    this.onResourceUpdate(dataToUpdate)
      .pipe(
        tap(() => {
          this.createOrUpdateTable(resource);
        }),
        catchError(() => {
          this.alertsService.addError('Error updating resource');

          return of(null);
        }),
      )
      .subscribe();
  }

  private createResourceAndSaveTable(): void {
    const resource: Omit<Resource, '_id'> = {
      name: this.resourceNameControl.value,
      enabled: true,
      capacity: {
        min: this.minCapacityControl.value,
        max: this.maxCapacityControl.value,
      },
      availability: {
        waitlist: this.publicCheckInControl.value,
        reservations: this.reservationsControl.value,
      },
      category: '',
      services: this.serviceControl.value,
      restaurant: this.locationId,
      description: '',
      order: this.resources.length + 1,
    };

    this.onResourceCreate(resource)
      .pipe(
        tap((createdResource) => {
          this.createOrUpdateTable(createdResource);
        }),
        catchError(() => {
          this.alertsService.addError('Error creating new resource');

          return of(null);
        }),
      )
      .subscribe();
  }

  private createOrUpdateTable(resource: Resource): void {
    const attributes = this.getAttributes(resource._id);
    const sectionId = this.sectionIdControl?.value;

    this.isEditMode
      ? this.onSave(this.tableId, this.floorPlanId, resource.name, attributes, sectionId)
      : this.onCreate(resource.name, attributes, sectionId);
    this.formClosed.emit(false);
  }

  private getAttributes(resourceId: string): TableAttributes {
    return {
      resourceId,
      shape: this.form.get('shape')?.value || Shapes.rectangular,
      size: this.form.get('size')?.value || TableSizes.custom,
      rotation: this.rotationControl?.value || 0,
      width: this.width || 50,
      height: this.height || 50,
      top: this.top || 1,
      left: this.left || 1,
    };
  }

  private resourceWasUpdated(resource: Resource): boolean {
    return Boolean(resource._id) && (
      (this.isEditMode && this.resourceNameControl.dirty) ||
      this.resourceIdControl.dirty ||
      this.minCapacityControl.dirty ||
      this.maxCapacityControl.dirty ||
      this.publicCheckInControl.dirty ||
      this.reservationsControl.dirty ||
      this.sectionIdControl.dirty ||
      this.serviceControl.dirty
    );
  }

  protected readonly angleChanged = angleChanged;
}
